import * as React from 'react'
import { classNames } from './helpers'

export interface DetailStageProps {
  /** Whatever fills the stage — a static image, or a poster that swaps to a player. */
  children?: React.ReactNode;
  className?: string;
}

export interface DetailSheetProps {
  /** The reading matter: title, synopsis, body, and any sections below. */
  children: React.ReactNode;
  className?: string;
}

/**
 * The stage of a detail page: a full-bleed 16:9 image box, capped at 80vh and
 * pulled up under the shell's fixed header. Shared by the watch and news detail
 * pages.
 *
 * A layout shell, not the content: the watch page fills it with a poster that
 * swaps to a `<Player>`, the news page with a static `<img>`. Keeping the box here
 * and the content in each pilet is what lets the two share the frame without this
 * component owning either one's behaviour — including the bottom scrim, which a
 * static image wants (seated against the sheet) but a live player does not, so the
 * caller hangs it off the positioned box.
 *
 * Contract: the page root must reserve `var(--page-top-inset)` of top padding —
 * the stage's negative top margin is that same value, and rides up into it to clear
 * the shell's fixed header. The two must cancel, so both read the one token rather
 * than restating the number.
 *
 * The ref lands on the box itself, so a caller can measure it or scroll it into
 * view (the watch page does that when playback starts).
 */
export const DetailStage = React.forwardRef<HTMLDivElement, DetailStageProps>(function DetailStage (
  { children, className }, ref
) {
  return <div ref={ref} className={classNames('mlsg-detail-stage', className)}>{children}</div>
})

/**
 * The reading matter of a detail page: a centred, measured column laid over the
 * bottom of the {@link DetailStage}, riding up so the stage runs behind its top
 * edge and the two read as one composition. Owns the surface, the measure, and the
 * overlap; the caller fills it and may nudge the overlap via `--mlsg-detail-overlap`.
 */
export function DetailSheet ({ children, className }: DetailSheetProps) {
  return <div className={classNames('mlsg-detail-sheet', className)}>{children}</div>
}
